import { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import Input from "../../atoms/Input";
import SingleSelect from "../../atoms/SingleSelect";
import MultiSelectDropdown from "../../atoms/MultiSelect";
import CustomDateRangePicker from "../../atoms/DatePicker";


const formatDisplayDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const Filter = ({ filterDataArr, handleFilterClick, handleExportData }) => {
  const [selectedFilterLabel, setSelectedFilterLabel] = useState(null);
  const [filterData, setFilterData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    setFilterData(
      filterDataArr.filter((el) => {
        // Handle date range filters
        if (el.type === "date") {
          return el.value?.startDate && el.value?.endDate;
        }
        // Handle array filters (multi-select)
        if (Array.isArray(el.value)) {
          return el.value.length > 0;
        }
        // Handle string/number filters
        return el.value !== "" && el.value !== null && el.value !== undefined;
      })
    );
  }, [filterDataArr]);


  const handleFilterLabel = (val) => {
    const newObj = filterDataArr.find((el) => el.id === val);

    if (newObj?.type === "date") {
      // Initialize date states if this is a date filter
      setStartDate(newObj.value?.startDate || null);
      setEndDate(newObj.value?.endDate || null);
    }

    setSelectedFilterLabel(newObj || null);
  };

  const handleAnsSwitch = (val) => {
    if (selectedFilterLabel) {
      setSelectedFilterLabel({ ...selectedFilterLabel, value: val });
    }
  };

  const handleApplyFilter = () => {
    const updatedFilterData = filterDataArr.map((el) => {
      if (selectedFilterLabel?.id === el.id) {
        if (el.type === "date") {
          // Handle date range selection
          el.value = {
            startDate: startDate,
            endDate: endDate,
          };
        } else if (
          el.type === "input" &&
          Array.isArray(el.value) &&
          typeof selectedFilterLabel.value === "string"
        ) {
          el.value = [...el.value, selectedFilterLabel.value];
        } else {
          el.value = selectedFilterLabel.value;
        }
      }
      return el;
    });

    handleFilterClick(updatedFilterData);
    setSelectedFilterLabel(null);
    setStartDate(null); // Reset dates after applying
    setEndDate(null);
  };

  const handleRefreshFilter = () => {
    setSelectedFilterLabel(null);
  };

  const handleClearFilter = (id) => {
    if (id === "all") {
      let updatedFilters = filterDataArr.map((el) => ({
        ...el,
        value: el.type === "date"
          ? { startDate: null, endDate: null }
          : Array.isArray(el.value)
            ? []
            : ""
      }));
      handleFilterClick(updatedFilters);
      setStartDate(null);  // Also reset local date states
      setEndDate(null);
    } else {
      let updatedFilters = filterDataArr.map((el) => {
        if (el.id === id) {
          if (el.type === "date") {
            setStartDate(null);  // Reset local date states for this filter
            setEndDate(null);
            return { ...el, value: { startDate: null, endDate: null } };
          }
          return { ...el, value: Array.isArray(el.value) ? [] : "" };
        }
        return el;
      });
      handleFilterClick(updatedFilters);
    }
  };

  const validatePositiveNumber = (value) => {
    // Allow empty input or only valid positive numbers (integer or decimal)
    if (value === "" || /^[+]?\d*\.?\d+$/.test(value)) {
      return value;
    }
    return selectedFilterLabel?.value;
  };

  const AnswerSwitch = ({ disabled }) => {
    switch (selectedFilterLabel?.type) {
      case "single-select":
        return (
          <SingleSelect
            label={selectedFilterLabel?.label}
            options={selectedFilterLabel?.dropArr || []}
            value={selectedFilterLabel?.value || ""}
            onChange={(e) => handleAnsSwitch(e.target.value)}
            disabled={disabled}
          />
        );
      case "multi-select":
        return (
          <MultiSelectDropdown
            label={selectedFilterLabel?.label}
            options={selectedFilterLabel?.dropArr || []}
            value={selectedFilterLabel?.value || []}
            onChange={(e) => handleAnsSwitch(e.target.value)}
            renderSelectedValues={(selected) => selected.join(", ")}
          />
        );
      case "input":
        return (
          <Input
            fullWidth
            value={selectedFilterLabel?.value || ""}
            onChange={(e) => handleAnsSwitch(e.target.value)}
            label={selectedFilterLabel?.label}
            placeholder={`Enter ${selectedFilterLabel?.label}`}
            disabled={disabled}
          />
        );
      case "input-number":
        return (
          <Input
            value={selectedFilterLabel?.value || ""}
            onChange={(e) =>
              handleAnsSwitch(validatePositiveNumber(e.target.value))
            }
            disabled={disabled}
            label={selectedFilterLabel?.label}
            placeholder="Enter Positive Number"
            error={
              isNaN(selectedFilterLabel?.value) ||
              selectedFilterLabel?.value <= 0
            }
            helperText={
              isNaN(selectedFilterLabel?.value) ||
                selectedFilterLabel?.value <= 0
                ? "Please enter a positive number."
                : ""
            }
          />
        );
      case "date":
        return (
          <div>
            <CustomDateRangePicker
              initialStartDate={startDate}
              initialEndDate={endDate}
              onDatesChange={({ startDate, endDate }) => {
                setStartDate(startDate);
                setEndDate(endDate);
              }}
            />
          </div>
        );
      default:
        return (
          <SingleSelect
            label={selectedFilterLabel?.label || "Select"}
            value={selectedFilterLabel?.value || ""}
            onChange={(e) => handleAnsSwitch(e.target.value)}
            options={selectedFilterLabel?.dropArr || []}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <Box pt={1} pb={0.5}>
      <Box sx={{ ...FilterBoxForm }}>
        <Box className="AnsBox" >
          <SingleSelect
            label="Select"
            value={selectedFilterLabel?.id || ""}
            onChange={(e) => handleFilterLabel(e.target.value)}
            options={filterDataArr
              ?.filter(el => !filterData.some(filter => filter.id === el.id))
              ?.map(el => ({
                value: el.id,
                label: el.label
              })) || []}
          />
        </Box>

        <Box className="" minWidth={'200px'}>
          {AnswerSwitch({ disabled: !selectedFilterLabel?.id })}
        </Box>
        <Box sx={{ width: "5%" }} display="flex" justifyContent="center">
          <Tooltip title={"Apply filter"} arrow>
            <IconButton onClick={handleApplyFilter}>
              <CheckCircleOutlineIcon style={{ color: '#000' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ width: "5%" }} display="flex" justifyContent="center">
          <Tooltip title={"Refresh"} arrow>
            <IconButton onClick={handleRefreshFilter}>
              <RefreshIcon style={{ color: '#000' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography
          onClick={() => handleClearFilter("all")}
          variant="subtitle1"
          sx={{
            cursor: "pointer",
            textDecorationLine: "underline",
            width: "10%",
            textAlign: "center",
          }}
        >
          Clear Filter
        </Typography>
        {handleExportData && (
          <Typography
            onClick={() => { handleExportData() }}
            variant="subtitle1"
            sx={{
              cursor: "pointer",
              textDecorationLine: "underline",
              width: "10%",
              textAlign: "center",
            }}
          >
            Export Data
          </Typography>
        )}
      </Box>

      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        overflow="auto"
        maxHeight={140}
      >
        {filterData.map((el) => (
          <Box key={el.id} display="flex" sx={{ ...sxChip }}>
            {el.type === "date" ? (
              <Typography variant="subtitle1">
                {`${el.label}: ${formatDisplayDate(el.value.startDate)} - ${formatDisplayDate(el.value.endDate)}`}
              </Typography>
            ) : Array.isArray(el.value) ? (
              <Typography variant="subtitle1">
                {`${el.label}: ${el.value.join(", ")}`}
              </Typography>
            ) : (
              <Typography variant="subtitle1">
                {`${el.label}: ${el.value}`}
              </Typography>
            )}
            <IconButton
              sx={{ p: 0, ml: 0.5 }}
              onClick={() => handleClearFilter(el.id)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const sxChip = {
  backgroundColor: "#FAAA2F",
  px: 1,
  py: 0.25,
  mt: 0.5,
  mb: 1,
  mr: 1,
  borderRadius: 5,
  "& h6": {
    color: "common.white",
    "&.MuiTypography-root": {
      fontSize: "0.9rem !important",
    },
  },
  "& svg": {
    ml: 0.5,
    fontSize: "1rem",
    color: "common.white",
  },
  "& p": {
    fontSize: "0.8rem",
    color: "#fff",
  },
};
const FilterBoxForm = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: '5px',
  ".AnsBox": {
    width: "20%",
  },
};
export default Filter;
